// src/app/pages/campus/CampusStudentsPage.tsx
import { useEffect, useState } from 'react';
import { PageTitle } from '../../../_metronic/layout/core';
import styleScss from './styles.module.scss';
import { useAuth } from '../../modules/auth';
import { LinearLoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import BackButton from '../../components/BackButton/BackButton';
import campusService, { Student } from './services/campusService';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    TablePagination,
    Stack,
    IconButton,
    Button,
    Alert,
    Snackbar,
    AlertTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import { initValue } from '../../util/Index';
import UseDidMountEffect from '../../modules/auth/core/UseDidMountEffect';
import { useParams } from 'react-router-dom';

interface CampusStudentsPageProps {
    courseId?: number;
    courseName?: string;
}

const CampusStudentsPage = () => {
    const { id } = useParams<{ id: string }>();
    const { isAdminRole, isCounselorCandidateRole, isCounselorEmployerRole } = useAuth();
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
    const [filter, setFilter] = useState<'all' | 'approved' | 'not_approved'>('all');
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState(true);
    const [alertMsg, setAlertMsg] = useState('');
    const [courseName, setCourseName] = useState<string>('');

    // Pagination
    const [total, setTotal] = useState<number>(0);
    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 10
    });

    useEffect(() => {
        if (id) {
            loadStudents();
            loadCourseName();
        }
    }, [id]);

    UseDidMountEffect(() => {
        loadStudents();
    }, [pagination.page, pagination.rowsPerPage, filter]);

    const loadCourseName = async () => {
        try {
            const courses = await campusService.getCourses({});
            const course = courses.courses.find((c: any) => c.id === parseInt(id!));
            if (course) {
                setCourseName(course.fullname);
            }
        } catch (error) {
            console.error('Error loading course name:', error);
        }
    };

    const loadStudents = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const data = await campusService.getCourseStudents(parseInt(id));
            setStudents(data.students);
            setTotal(data.students.length);
        } catch (error: any) {
            setAlert(false);
            setAlertMsg(error.response?.data?.error || 'Error al cargar los participantes');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (newPage: any) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleChangeRowsPerPage = (event: any) => {
        setPagination({
            page: 0,
            rowsPerPage: parseInt(event.target.value, 10)
        });
    };

    const handleFirstPageButtonClick = () => {
        handleChangePage(0);
    };

    const handleLastPageButtonClick = () => {
        handleChangePage(Math.ceil(total / pagination.rowsPerPage) - 1);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const toggleStudentSelection = (moodleId: number) => {
        setSelectedStudents(prev =>
            prev.includes(moodleId)
                ? prev.filter(id => id !== moodleId)
                : [...prev, moodleId]
        );
    };

    const toggleAllStudents = () => {
        const filtered = getFilteredStudents();
        const allIds = filtered.map(s => s.moodle_id);
        if (selectedStudents.length === allIds.length) {
            setSelectedStudents([]);
        } else {
            setSelectedStudents(allIds);
        }
    };

    const clearSelection = () => {
        setSelectedStudents([]);
    };

    const getFilteredStudents = () => {
        if (filter === 'approved') return students.filter(s => s.is_approved);
        if (filter === 'not_approved') return students.filter(s => !s.is_approved);
        return students;
    };

    const handleDownloadCertificate = async (studentId: number) => {
        try {
            const blob = await campusService.generateCertificate(studentId, parseInt(id!));
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const student = students.find(s => s.moodle_id === studentId);
            link.download = `certificado_${student?.fullname.replace(/\s/g, '_')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setAlert(true);
            setAlertMsg(` Certificado descargado para ${student?.fullname}`);
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
        } catch (error: any) {
            setAlert(false);
            setAlertMsg(error.response?.data?.error || 'Error al descargar el certificado');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
        }
    };

    const handleSendCertificate = async (studentId: number) => {
        try {
            const student = students.find(s => s.moodle_id === studentId);
            if (!student) return;
            await campusService.sendCertificate(studentId, parseInt(id!));
            setAlert(true);
            setAlertMsg(` Certificado enviado a ${student.fullname}`);
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
        } catch (error: any) {
            setAlert(false);
            setAlertMsg(error.response?.data?.error || 'Error al enviar el certificado');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
        }
    };

    const handleBatchDownload = async () => {
        if (selectedStudents.length === 0) {
            setAlert(false);
            setAlertMsg('Selecciona al menos un estudiante');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
            return;
        }

        const selected = students.filter(s => selectedStudents.includes(s.moodle_id));
        const approved = selected.filter(s => s.is_approved);

        if (approved.length === 0) {
            setAlert(false);
            setAlertMsg('Los estudiantes seleccionados no han aprobado el curso');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
            return;
        }

        setLoading(true);
        try {
            for (const student of approved) {
                const blob = await campusService.generateCertificate(student.moodle_id, parseInt(id!));
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `certificado_${student.fullname.replace(/\s/g, '_')}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                await new Promise(resolve => setTimeout(resolve, 300));
            }
            setAlert(true);
            setAlertMsg(` ${approved.length} certificado(s) descargado(s)`);
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
            clearSelection();
        } catch (error: any) {
            setAlert(false);
            setAlertMsg(error.response?.data?.error || 'Error al descargar los certificados');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleBatchSend = async () => {
        if (selectedStudents.length === 0) {
            setAlert(false);
            setAlertMsg('Selecciona al menos un estudiante');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
            return;
        }

        const selected = students.filter(s => selectedStudents.includes(s.moodle_id));
        const approved = selected.filter(s => s.is_approved && s.email);

        if (approved.length === 0) {
            setAlert(false);
            setAlertMsg('Los estudiantes seleccionados no tienen email o no han aprobado');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
            return;
        }

        setLoading(true);
        try {
            const result = await campusService.sendBatchCertificates(
                parseInt(id!),
                approved.map(s => s.moodle_id)
            );
            setAlert(true);
            setAlertMsg(` ${result.sent} certificado(s) enviado(s), ${result.failed} fallido(s)`);
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
            clearSelection();
        } catch (error: any) {
            setAlert(false);
            setAlertMsg(error.response?.data?.error || 'Error al enviar los certificados');
            setOpen(true);
            setTimeout(() => setOpen(false), 3000);
        } finally {
            setLoading(false);
        }
    };

    const filteredStudents = getFilteredStudents();
    const approvedCount = students.filter(s => s.is_approved).length;

    return (
        <>
            <PageTitle breadcrumbs={[]}>Participantes - {courseName || 'Curso'}</PageTitle>

            <div className='p-4 shadow-4 rounded-3 mt-15 row' style={{ backgroundColor: '#FFFF' }}>
                <div className='col-md-1 row mb-4' style={{ alignItems: 'center' }}>
                    <div className='input-group d-flex' style={{ width: '100%', alignItems: 'center' }}>
                        {/* BackButton */}
                        <div style={{ padding: '10px 0px 0px 15px' }}>
                            <div className='col-xs-2 col-sm-1 col-md-1'
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                <BackButton />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filtros y acciones */}
                <div className='col-md-11 row mb-4' style={{ alignItems: 'center' }}>
                    <div className='input-group d-flex' style={{ width: '100%', alignItems: 'center', padding: '0 15px', flexWrap: 'wrap', gap: '8px' }}>
                        {/* Filtro de estado */}
                        <div className='col-lg-2 col-xs-12 col-sm-4 col-md-2' style={{ padding: '10px 8px 1px 0px' }}>
                            <FormControl fullWidth size="small">
                                <Select
                                    labelId='filter-label'
                                    label='Filtro'
                                    value={filter}
                                    style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}
                                    onChange={(e) => setFilter(e.target.value as any)}
                                >
                                    <MenuItem value='all'>Todos ({students.length})</MenuItem>
                                    <MenuItem value='approved'>Aprobados ({approvedCount})</MenuItem>
                                    <MenuItem value='not_approved'>No aprobados ({students.length - approvedCount})</MenuItem>
                                </Select>
                            </FormControl>
                        </div>

                        <div className='col-lg-3 col-xs-12 col-sm-4 col-md-3' style={{ padding: '10px 8px 1px 0px' }}>
                            <span className='text-muted' style={{ fontFamily: 'Poppins', fontSize: '14px' }}>
                                <i className='bi bi-check-square me-1'></i>
                                {selectedStudents.length} seleccionados
                            </span>
                        </div>

                        {/* Botones de acciones */}
                        <div className='col-lg-7 col-xs-12 col-sm-12 col-md-7' style={{ padding: '10px 0px 0px 0px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <Button
                                variant='contained'
                                size='small'
                                sx={{ background: '#28a745', textTransform: 'none' }}
                                onClick={handleBatchDownload}
                                disabled={selectedStudents.length === 0 || loading}
                            >
                                <DownloadIcon fontSize='small' sx={{ mr: 1 }} />
                                Descargar seleccionados
                            </Button>
                            <Button
                                variant='contained'
                                size='small'
                                sx={{ background: '#0A4396', textTransform: 'none' }}
                                onClick={handleBatchSend}
                                disabled={selectedStudents.length === 0 || loading}
                            >
                                <EmailIcon fontSize='small' sx={{ mr: 1 }} />
                                Enviar seleccionados
                            </Button>
                            {selectedStudents.length > 0 && (
                                <Button
                                    variant='outlined'
                                    size='small'
                                    onClick={clearSelection}
                                    sx={{ textTransform: 'none' }}
                                >
                                    <i className='bi bi-x-circle me-1'></i>
                                    Limpiar selección
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {loading && <LinearLoadingSpinner isLoading={loading} />}

                {/* Tabla de estudiantes */}
                {!loading && (
                    <div className={`${styleScss['table-limit']}`} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
                        <Stack padding={2}>
                            <TableContainer component={Paper} style={{ overflowY: 'visible', overflowWrap: 'break-word' }}>
                                <Table style={{ overflowY: 'visible' }}>
                                    <TableHead className={`${styleScss['th-custom']}`}>
                                        <TableRow>
                                            <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                                                <Checkbox
                                                    checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                                                    onChange={toggleAllStudents}
                                                />
                                            </TableCell>
                                            <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                                                <h4>Estudiante</h4>
                                            </TableCell>
                                            <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                                                <h4>Tipo Documento</h4>
                                            </TableCell>
                                            <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                                                <h4>Documento</h4>
                                            </TableCell>
                                            <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                                                <h4>Email</h4>
                                            </TableCell>
                                            <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                                                <h4>Progreso</h4>
                                            </TableCell>
                                            <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                                                <h4>Estado</h4>
                                            </TableCell>
                                            <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                                                <h4>Acciones</h4>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredStudents.map((student) => (
                                            <TableRow key={student.moodle_id}>
                                                <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                                                    <Checkbox
                                                        checked={selectedStudents.includes(student.moodle_id)}
                                                        onChange={() => toggleStudentSelection(student.moodle_id)}
                                                        disabled={!student.is_approved}
                                                        title={!student.is_approved ? 'Solo estudiantes aprobados pueden ser seleccionados' : ''}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                                                    <strong>{student.fullname}</strong>
                                                </TableCell>
                                                <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                                                    <span className='badge bg-light-info text-dark'>
                                                        {student.document_type || 'N/A'}
                                                    </span>
                                                </TableCell>

                                                {/* Número de documento */}
                                                <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                                                    <span className='fw-semibold'>
                                                        {student.document_number || 'N/A'}
                                                    </span>
                                                </TableCell>
                                                <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                                                    <a href={`mailto:${student.email}`} className='text-primary'>
                                                        {student.email || 'Sin email'}
                                                    </a>
                                                </TableCell>
                                                <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                                                    <div className='d-flex align-items-center gap-2 justify-content-center'>
                                                        <div className='progress w-75' style={{ height: '8px' }}>
                                                            <div
                                                                className={`progress-bar ${student.is_approved ? 'bg-success' : 'bg-primary'}`}
                                                                style={{ width: `${Math.round(student.percentage)}%` }}
                                                                role="progressbar"
                                                                aria-valuenow={Math.round(student.percentage)}
                                                                aria-valuemin={0}
                                                                aria-valuemax={100}
                                                            ></div>
                                                        </div>
                                                        <span className='fw-semibold'>{Math.round(student.percentage)}%</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                                                    <span className={`badge ${student.is_approved ? 'bg-success' : 'bg-danger'}`}>
                                                        <i className={`bi ${student.is_approved ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                                                        {student.is_approved ? 'Aprobado' : 'No aprobado'}
                                                    </span>
                                                </TableCell>
                                                <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                                                    <Tooltip title="Descargar certificado">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => handleDownloadCertificate(student.moodle_id)}
                                                                disabled={!student.is_approved}
                                                            >
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>

                                    {filteredStudents.length === 0 && (
                                        <tfoot>
                                            <TableRow>
                                                <TableCell colSpan={7}>
                                                    <p className='text-center' style={{ width: '100%', textAlign: 'center' }}>
                                                        {filter === 'all' && 'No hay participantes en este curso'}
                                                        {filter === 'approved' && 'No hay participantes aprobados'}
                                                        {filter === 'not_approved' && 'No hay participantes no aprobados'}
                                                    </p>
                                                </TableCell>
                                            </TableRow>
                                        </tfoot>
                                    )}
                                </Table>

                                <div style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}>
                                    <TablePagination
                                        rowsPerPageOptions={[10, 20, 50, 100]}
                                        component="div"
                                        style={{ overflow: 'visible', fontFamily: 'Poppins', fontSize: '14.5px' }}
                                        count={total}
                                        rowsPerPage={pagination.rowsPerPage}
                                        page={pagination.page}
                                        onPageChange={(event, newPage) => handleChangePage(newPage)}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        labelRowsPerPage="Filas por página:"
                                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                                        ActionsComponent={({ onPageChange, count, rowsPerPage }) => (
                                            <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'Poppins', fontSize: '14.5px' }}>
                                                <IconButton
                                                    onClick={handleFirstPageButtonClick}
                                                    disabled={pagination.page === 0}
                                                    aria-label="first page"
                                                >
                                                    <FirstPageIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => onPageChange(null, pagination.page - 1)}
                                                    disabled={pagination.page === 0}
                                                    aria-label="previous page"
                                                >
                                                    <ChevronLeftIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => onPageChange(null, pagination.page + 1)}
                                                    disabled={pagination.page >= Math.ceil(count / rowsPerPage) - 1}
                                                    aria-label="next page"
                                                >
                                                    <ChevronRightIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={handleLastPageButtonClick}
                                                    disabled={pagination.page >= Math.ceil(count / rowsPerPage) - 1}
                                                    aria-label="last page"
                                                >
                                                    <LastPageIcon />
                                                </IconButton>
                                            </div>
                                        )}
                                    />
                                </div>
                            </TableContainer>
                        </Stack>
                    </div>
                )}
            </div>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={open}
                onClose={handleClose}
            >
                <Alert
                    onClose={handleClose}
                    variant={'filled'}
                    severity={alert ? 'success' : 'error'}
                    sx={{ whiteSpace: 'pre-line' }}
                >
                    {alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
                    {alertMsg}
                </Alert>
            </Snackbar>
        </>
    );
};

export default CampusStudentsPage;